import React from 'react';
import {createStyles, makeStyles, Theme, useTheme} from '@material-ui/core/styles';
import {
    Box,
    Card,
    Chip,
    FormControl,
    Input,
    InputLabel,
    MenuItem,
    Select,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TablePagination,
    TableRow,
    Typography
} from '@material-ui/core';
import {apiRequest} from "../util/util";
import {SignupPopup} from "./SignupPopup";
import PayMe from "./PayMe";
import track from "../util/frontend_analytics";
import StoryItem from "./StoryItem";

const BLOCK_SIZE = 10;

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        cardContent: {
            padding: theme.spacing(3),
        },
        formControl: {
            width: '100%',
            marginTop: theme.spacing(2),
        },
        chips: {
            display: 'flex',
            flexWrap: 'wrap',
        },
        chip: {
            margin: theme.spacing(1),
        },
    })
);

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
    PaperProps: {
        style: {
            maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
        },
    },
};

const tags = [
    "straight", "lesbian", "cuckold", "gay male", "bdsm", "orgy", "interracial",
    "fetish", "coercion", "drugs", "incest", "college", "domination", "submissive",
    "older man", "older woman", "taboo", "public", "threesome", "supernatural",
    "party", "bus", "gym", "bar", "sauna", "office", "prison", "doctor"
];

interface StoryContent {
    storyId: string;
    tags: string[];
    title: string;
    text: string;
    sections: any[];
}

interface StoryItemProps {
    storyId: string;
    tags: string[];
    title: string;
    text: string | null;
    setSignupRequired: (value: boolean) => void;
    setPayMeActive: (value: boolean) => void;
}

export default function StoryControl() {
    const classes = useStyles();
    const theme = useTheme();

    const [selectedTags, setSelectedTags] = React.useState<string[]>([]);
    const [contentItems, setContentItems] = React.useState<StoryContent[]>([]);
    const [contentItemCount, setContentItemCount] = React.useState<number>(0);
    const [currentOffset, setCurrentOffset] = React.useState<number>(0);
    const [currentPage, setCurrentPage] = React.useState<number>(0);
    const [hidden, setHidden] = React.useState<boolean>(false);

    const [signupRequireActive, setSignupRequired] = React.useState(false);
    const [payMeActive, setPayMeActive] = React.useState(false);

    React.useEffect(() => {
        // On first load, retrieve the stories
        getContent(selectedTags);
    }, []);

    const handleChangePage = (
        event: React.MouseEvent<HTMLButtonElement> | null,
        newPage: number
    ) => {
        setCurrentPage(newPage);
        const newOffset = newPage * BLOCK_SIZE;
        setCurrentOffset(newOffset);
        getContent(selectedTags, newOffset);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        // Typically used to adjust page size, but we’re keeping it fixed
        setCurrentPage(0);
        setCurrentOffset(0);
    };

    async function getContent(selectedTags: string[], offset: number = 0) {
        try {
            const data = await apiRequest('ml', 'POST', {
                target: 'list-public-stories',
                theme: 'erotica',
                tags: selectedTags,
                offset: offset,
                blockSize: BLOCK_SIZE,
                returnSections: true,
            });

            if (selectedTags.length > 0) {
                track("list_stories", {
                    firstTag: selectedTags[0],
                    tags: selectedTags,
                    branch: process.env.BRANCH || null
                });
            }

            setContentItems(data.stories);
            setContentItemCount(data.numStories);
        } catch (error) {
            console.error("Error fetching stories: ", error);
        }
    }

    const handleTagChange = async (event: React.ChangeEvent<{ value: unknown }>) => {
        const newSelectedTags = event.target.value as string[];
        setSelectedTags(newSelectedTags);
        setCurrentPage(0);
        setCurrentOffset(0);
        await getContent(newSelectedTags, 0);
    };

    if (hidden) {
        return <div/>;
    }

    return (
        <div>
            <SignupPopup
                setSignupModal={setSignupRequired}
                signupModal={signupRequireActive}
            />

            {payMeActive && (
                <PayMe
                    open={payMeActive}
                    dismissCallback={() => setPayMeActive(false)}
                    runOut={true}
                />
            )}

            <Card style={{marginTop: 20, padding: 10}} elevation={10}>
                <Box mt={2}>
                    <Typography variant="h6">
                        Choose one or more tags for your story
                    </Typography>
                </Box>

                <FormControl className={classes.formControl}>
                    <InputLabel id="tag-select-label">Story Tags</InputLabel>
                    <Select
                        labelId="tag-select-label"
                        id="tag-select"
                        multiple
                        value={selectedTags}
                        onChange={handleTagChange}
                        input={<Input/>}
                        renderValue={(selected) => (
                            <div className={classes.chips}>
                                {(selected as string[]).length > 0 ? (
                                    (selected as string[]).map((value) => (
                                        <Chip key={value} label={value} className={classes.chip}/>
                                    ))
                                ) : (
                                    <div>No Tags Selected</div>
                                )}
                            </div>
                        )}
                        MenuProps={MenuProps}
                    >
                        {tags.map((tag) => (
                            <MenuItem
                                key={tag}
                                value={tag}
                                style={{
                                    fontWeight:
                                        selectedTags.indexOf(tag) === -1
                                            ? theme.typography.fontWeightRegular
                                            : theme.typography.fontWeightMedium
                                }}
                            >
                                {tag}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                {contentItems.length > 0 && (
                    <Box mt={2}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Title</TableCell>
                                    <TableCell>Snippet</TableCell>
                                    <TableCell>Tags</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {contentItems.map((storyContent) => (
                                    <StoryItem
                                        key={storyContent.storyId}
                                        storyId={storyContent.storyId}
                                        tags={storyContent.tags}
                                        title={storyContent.title}
                                        text={storyContent.sections ? storyContent.sections[0].text : null}
                                        setSignupRequired={setSignupRequired}
                                        setPayMeActive={setPayMeActive}
                                    />
                                ))}
                            </TableBody>
                        </Table>

                        <TablePagination
                            rowsPerPageOptions={[BLOCK_SIZE]}
                            component="div"
                            count={contentItemCount || 0}
                            rowsPerPage={BLOCK_SIZE}
                            page={currentPage}
                            onPageChange={handleChangePage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                        />
                    </Box>
                )}
            </Card>
        </div>
    );
}
